import {
  DocumentNode,
  NetworkStatus,
  NoInfer,
  OperationVariables,
  QueryHookOptions,
  QueryResult,
  useQuery,
} from '@apollo/client';
import { useCallback } from 'react';

import { CursorPaging, PageInfo } from '../graphql';

type BaseCursorQueryResult = {
  pageInfo: PageInfo;
  edges: { node: unknown }[];
};

type UseCursorPaginatedQueryArguments<TData> = {
  getConnection: (data: TData) => BaseCursorQueryResult;
  numElementsPerFetch?: number;
  numElementsInitialFetch?: number;
};

export interface UseCursorPaginatedQueryReturnType<TData, VariablesType extends { paging?: CursorPaging }> extends Pick<
  QueryResult<TData, VariablesType>,
  'data' | 'loading' | 'error'
> {
  hasNextPage: boolean;
  // promise returns once items have finished loading
  onLoadMoreItems: () => Promise<void>;
  isLoadingMore: boolean;
}

/**
 * `useCursorPaginatedQuery` is a custom hook for handling cursor-based pagination in GraphQL queries.
 *
 * @param query - The GraphQL query to be executed.
 * @param options - The options to be passed to the GraphQL query such as variables.
 * @param paginationConfig - Additional configuration for the query.
 * @param paginationConfig.getConnection - Getter function to retrieve the connection from the query result
 * @param paginationConfig.numElementsPerFetch - The number of elements to fetch in each subsequent requests after initial request
 * @param paginationConfig.numElementsInitialFetch - The number of elements to fetch in the initial query. It will default to numElementsPerFetch if not provided
 */
export const useCursorPaginatedQuery = <TData, VariablesType extends OperationVariables>(
  query: DocumentNode,
  options: QueryHookOptions<NoInfer<TData>, NoInfer<VariablesType>> &
    ({ variables: Omit<VariablesType, 'paging'> } | { skip: true }),
  {
    numElementsPerFetch = 20,
    numElementsInitialFetch = numElementsPerFetch,
    getConnection,
  }: UseCursorPaginatedQueryArguments<TData>,
): UseCursorPaginatedQueryReturnType<TData, VariablesType> => {
  const queryOptions = options.skip
    ? { skip: true }
    : {
        ...options,
        variables: {
          ...options.variables,
          paging: {
            first: numElementsInitialFetch,
          },
        },
        // required in order for loading to be true when we are loading more items
        notifyOnNetworkStatusChange: true,
      };
  // to understand pagination query strategy see https://www.apollographql.com/docs/react/pagination/core-api
  const { data, error, fetchMore, networkStatus } = useQuery<TData, VariablesType>(query, queryOptions);
  const { pageInfo } = data ? getConnection(data) : { pageInfo: null };

  const loading = networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables;
  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;

  const onLoadMoreItems = useCallback(async (): Promise<void> => {
    if (isLoadingMore || !pageInfo || !pageInfo.hasNextPage) {
      return Promise.resolve();
    }
    await fetchMore({
      variables: {
        paging: {
          first: numElementsPerFetch,
          after: pageInfo.endCursor,
        },
      },
    });
  }, [isLoadingMore, pageInfo, fetchMore, numElementsPerFetch]);

  return {
    data,
    hasNextPage: !!pageInfo?.hasNextPage,
    onLoadMoreItems,
    loading,
    error,
    isLoadingMore,
  };
};
